/* eslint camelcase: 0 */
import { APP_BASE_HREF, CommonModule, DOCUMENT } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, TemplateRef, ViewChild, ViewContainerRef, inject, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { AppHeaderComponent } from '@components/app-header/app-header.component';
import { AppSideNavComponent } from '@components/app-side-nav/app-side-nav.component';
import { FeedbackWidgetComponent } from '@components/feedback/feedback-widget.component';
import { UbNotificationsComponent } from '@components/storage-accounts/ub-notifications/ub-notifications.component';
import { ForceMSP2FaModalComponent } from '@components/tfa/components/force-msp-2fa-modal/force-msp-2fa-modal.component';
import { UnableDeliverEmailsAlertComponent } from '@components/unable-deliver-emails-alert/unable-deliver-emails-alert.component';
import { UnsupportedVersionComponent } from '@components/unsupported-version/unsupported-version.component';
import { EmailServiceStatusComponent } from '@modules/email-service-status/email-service-status.component';
import { InteractionEvent } from '@modules/empty-page/InteractionEvent';
import { EmptyPageComponent } from '@modules/empty-page/empty-page.component';
import { SupportChatComponent } from '@modules/support-chat/support-chat.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ComputersAbstractService } from '@services';
import { NavLayoutService } from '@services/nav-layout.service';
import { ProviderMessageService, ProviderMessageStatus } from '@services/provider-message.service';
import { ChatVisibilityService } from '@services/services/chat-visibility.service';
import { AppLayoutService } from '@shared/_layout/app-layout/app-layout.service';
import { AbilityService, CanPipe, EventTypes } from 'ability';
import { I18NextPipe, I18NextService } from 'angular-i18next';
import { isNil } from 'lodash';
import { Alert, AlertService, ButtonComponent, MbsPopupType, MbsSize, ModalService, Toast, ToastComponent, ToastService } from 'mbs-ui-kit';
import { AlertType } from 'mbs-ui-kit/alert/alert.model';
import { AllowOfflineEditModalComponent } from 'mbs-ui-shared@components/allow-offline-edit-modal/allow-offline-edit-modal.component';
import { AppFooterComponent } from 'mbs-ui-shared@components/app-footer/app-footer.component';
import { ForceTfaAlertComponent } from 'mbs-ui-shared@components/force-tfa-alert/force-tfa-alert.component';
import { AutoRenewalAlertComponent } from 'mbs-ui-shared@components/licenses/components/auto-renewal-alert/auto-renewal-alert.component';
import { ApplicationStateFacade } from 'mbs-ui-shared@facades/application.facade';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { LicensesFacade } from 'mbs-ui-shared@facades/licenses.facade';
import { Administrator, PermissionsEnum, TFAAuthType, UiStorageKey } from 'mbs-ui-shared@models';
import { AgentType, OsType } from 'mbs-ui-shared@models/Computer';
import { SignalRService } from 'mbs-ui-shared@modules';
import { AppPersistentStateService } from 'mbs-ui-shared@services/app-persistent-state.service';
import { ConfigurationService } from 'mbs-ui-shared@services/configuration.service';
import { MaintenanceService } from 'mbs-ui-shared@services/maintenance.service';
import { MobileApplicationsService } from 'mbs-ui-shared@services/mobile-applications.service';
import { TFAService } from 'mbs-ui-shared@services/tfa.service';
import { onlyDateFormat } from 'mbs-ui-shared@utils/date';
import moment from 'moment';
import { EMPTY, combineLatest, from, noop, of } from 'rxjs';
import { catchError, filter, first, switchMap, take, tap } from 'rxjs/operators';
import { adaptiveLayoutUrls, excludedFooterUrls } from './app-layout.constants';

// eslint-disable-next-line no-var
declare var sleek: {
  data: any;
  setUser(data: any): void;
  open(view, callback: any): void;
  showButton(): void;
  hideButton(): void;
  close(): void;
};

@UntilDestroy()
@Component({
  selector: 'app-app-layout',
  templateUrl: './app-layout.component.html',
  imports: [
    AppFooterComponent,
    AppHeaderComponent,
    AppSideNavComponent,
    AutoRenewalAlertComponent,
    ButtonComponent,
    CommonModule,
    EmailServiceStatusComponent,
    FeedbackWidgetComponent,
    ForceTfaAlertComponent,
    I18NextPipe,
    RouterOutlet,
    ToastComponent,
    UbNotificationsComponent,
    UnableDeliverEmailsAlertComponent,
    UnsupportedVersionComponent,
    SupportChatComponent,
    CanPipe
  ]
})
export class AppLayoutComponent implements OnInit {
  private alertService = inject(AlertService);
  private titleService = inject(Title);
  private ability = inject(AbilityService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private dom = inject<Document>(DOCUMENT);
  private auth = inject(AuthFacade);
  private appBaseHref = inject(APP_BASE_HREF);
  i18nextService = inject(I18NextService);
  i18nPipe = inject(I18NextPipe);
  private appPersistent = inject(AppPersistentStateService);
  private cdr = inject(ChangeDetectorRef);
  private appLayoutService = inject(AppLayoutService);
  private modalService = inject(ModalService);
  private licensesFacade = inject(LicensesFacade);
  private tFAService = inject(TFAService);
  private applicationFacade = inject(ApplicationStateFacade);
  private config = inject(ConfigurationService);
  private signalR = inject(SignalRService);
  private computersService = inject(ComputersAbstractService);
  private maintenanceService = inject(MaintenanceService);
  private mobileApplicationsService = inject(MobileApplicationsService);
  private viewContainerRef = inject(ViewContainerRef);
  private toastService = inject(ToastService);
  private chatVisibility = inject(ChatVisibilityService);
  private providerMessageService = inject(ProviderMessageService);
  protected navLayout = inject(NavLayoutService);

  public readonly size = MbsSize;
  public readonly PermissionsEnum = PermissionsEnum;

  public showFooter = signal(true);
  public adaptivePage = signal(false);

  public activateRouteComponent: any;
  public currentUser: Administrator;

  public permissionFlags = this.licensesFacade.permissionFlags;

  public disconnectAlert: Alert;

  private isIE;
  private readonly DAYS_FOR_SHOW_MODAL_AGAIN = 14;
  private readonly NUMBER_OF_MODAL_SHOW = 3;

  protected readonly AlertType = AlertType;

  @ViewChild('timezoneAlertTemplate', { static: true }) timezoneAlertTemplate: TemplateRef<any>;
  @ViewChild('disconnectAlertTemplate', { static: true }) disconnectAlertTemplate: TemplateRef<any>;

  constructor() {
    const ability = this.ability;
    const dom = this.dom;
    const auth = this.auth;
    const appBaseHref = this.appBaseHref;

    this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe((event) => {
      this.showFooter.set(this.isFooterAllowed());
      this.adaptivePage.set(this.isPageAdaptive());
      this.handleUpdateTitle();
    });
    ability.on(EventTypes.Update, () => {
      this.handleUpdateTitle();
    });
    this.handleUpdateTitle();

    // check and send to parent window if double iframe
    window.top != window &&
      window.top.postMessage({ msg: InteractionEvent.IFRAME_IN_IFRAME, currentRoute: dom.location.pathname.replace(appBaseHref, '') }, '*');

    auth.currentUser$
      .pipe(
        first((user: Administrator) => !!user && !!user.ProviderInfo),
        untilDestroyed(this)
      )
      .subscribe((user: Administrator) => (this.currentUser = user));

    this.signalR.stopNonAuthorizedConnection();
    this.signalR.startAuthorizedConnection();
  }

  private isFooterAllowed(): boolean {
    if (excludedFooterUrls.some((url) => this.router.url.includes(url))) return false;

    const child = this.route.children[0];

    return !(child.routeConfig.component && child.routeConfig.component === EmptyPageComponent);
  }

  private isPageAdaptive(): boolean {
    return adaptiveLayoutUrls.some((url) => this.router.url.includes(url));
  }

  ngOnInit(): void {
    const revokedSessions = this.auth.credentials$.getValue()?.revokedSessions;

    if (revokedSessions?.length) {
      setTimeout(() => {
        this.toastService.toast(
          new Toast({
            header: this.i18nPipe.transform('mbs-ui:revokedSessionsToast.title'),
            icon: true,
            content: this.i18nPipe.transform('mbs-ui:revokedSessionsToast.text'),
            type: MbsPopupType.warning
          })
        );
      }, 3000);
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this.isIE = Boolean(this.dom.documentMode); // document mode implements only in IE

    this.appLayoutService.showFooter$.pipe(untilDestroyed(this)).subscribe((showFooter) => {
      if (isNil(showFooter)) return;
      this.showFooter.set(showFooter);
    });

    if (localStorage.getItem('after2FALogin') === 'true' && this.ability.can('read', PermissionsEnum.HelpMarketing)) {
      localStorage.setItem('after2FALogin', 'false');
      this.showForceMbs2FAModal();
    }

    if (localStorage.getItem('showAllowOfflineEdit')) {
      this.computersService
        .getComputers({ os: [OsType.windows], appIds: [AgentType.Backup], offset: 0, limit: 1 })
        .pipe(
          tap((result) => !result?.data?.length && localStorage.removeItem('showAllowOfflineEdit')),
          untilDestroyed(this),
          first()
        )
        .subscribe({
          next: (result) => result?.data?.length && this.showAllowOfflineEdit()
        });
    }

    this.licensesFacade.loadPermissionFlags();

    this.showProviderMessage();
  }

  private showProviderMessage(): void {
    if (!this.currentUser?.IsProvider) return;

    this.providerMessageService
      .getMessage()
      .pipe(
        filter((msg) => !!msg?.title || !!msg?.message),
        switchMap((msg) =>
          from(
            this.modalService.open(
              {
                header: { title: msg.title },
                footer: {
                  okButton: { text: this.i18nPipe.transform('shared:buttons.ok'), type: 'primary' },
                  cancelButton: { text: this.i18nPipe.transform('shared:buttons.remindMeLater') }
                }
              },
              msg.message
            )
          ).pipe(catchError(() => of(false)))
        ),
        switchMap((confirmed) =>
          this.providerMessageService.updateStatus(confirmed ? ProviderMessageStatus.Read : ProviderMessageStatus.Delayed)
        ),
        catchError(() => EMPTY),
        untilDestroyed(this)
      )
      .subscribe();
  }

  showAllowOfflineEdit(): void {
    if (!this.currentUser?.IsProvider || this.ability.can('read', 'AllowOfflineSettingsEdit')) return;

    localStorage.setItem('isOpenAllowOfflineEdit', 'true');

    this.applicationFacade
      .getUiStorage()
      .pipe(take(1), untilDestroyed(this))
      .subscribe({
        next: (storage) => {
          const allowOfflineEdit = storage?.AllowOfflineEdit ? JSON.parse(storage.AllowOfflineEdit) : null;
          const currentAllowSettings = allowOfflineEdit?.[this.currentUser.Id];

          localStorage.removeItem('showAllowOfflineEdit');

          if (
            storage?.ShowOfflineEditBanner !== 'true' ||
            currentAllowSettings?.showed > this.NUMBER_OF_MODAL_SHOW ||
            (!isNil(currentAllowSettings) &&
              moment().diff(moment(allowOfflineEdit[this.currentUser.Id].lastShow, onlyDateFormat), 'days') <
                this.DAYS_FOR_SHOW_MODAL_AGAIN)
          ) {
            return localStorage.removeItem('isOpenAllowOfflineEdit');
          }

          const settings = {
            data: { allowOfflineEdit, id: this.currentUser.Id, simpleMode: false },
            header: { headerCustomClasses: 'p-0 m-0', showCloseCross: false }
          };

          this.modalService.openCustom(AllowOfflineEditModalComponent, settings).then(noop).catch(noop);
        },
        error: () => localStorage.removeItem('isOpenAllowOfflineEdit')
      });
  }

  ngAfterViewInit(): void {
    if (this.isIE) this.dom.documentElement.classList.add('-unsupported-browser');
    this.auth.fetchCurrentUser$.pipe(untilDestroyed(this)).subscribe((user) => {
      this.showTimezoneAlert(user);
    });

    this.handleSignalDisconnect();
    this.showMaintenanceNotifications();
    this.showInstallAppBanner();
  }

  showForceMbs2FAModal() {
    combineLatest([this.tFAService.getAuthType(), this.applicationFacade.getUiStorage().pipe(take(1))])
      .pipe(untilDestroyed(this))
      .subscribe({
        next: ([type, storage]) => {
          const showModal =
            this.config.get('showForceMSP2FAModal') &&
            type !== TFAAuthType.MbsApp &&
            storage[UiStorageKey.HideMSP2FAModal] !== 'true' &&
            this.ability.can('read', PermissionsEnum.ShowMSP360ControlMobileApplication);

          if (showModal) {
            this.modalService.openCustom(ForceMSP2FaModalComponent).finally(noop);
          }
        }
      });
  }

  changeTimezoneClickHandler(): void {
    this.router.navigate(['/AP/Settings']);
  }

  handleUpdateTitle(): void {
    if (this.ability.can('read', 'MBS')) {
      this.titleService.setTitle(this.i18nPipe.transform('mbs-ui:appTitle'));
    }
  }

  handleActiveRoute(componentInstance?: any): void {
    Promise.resolve()
      .then(() => {
        this.activateRouteComponent = componentInstance;
      })
      .catch(noop);
  }

  startSignalConnection() {
    this.signalR.restartAuthorizedConnection();

    this.alertService.clearById(this.disconnectAlert.id);
  }

  handleSignalDisconnect() {
    this.signalR.onclose.pipe(untilDestroyed(this)).subscribe((error) => {
      console.log(`${new Date()}: signalR.onclose : ${error}`);
      this.showDisconnectAlert();
    });
  }

  showDisconnectAlert() {
    this.disconnectAlert = new Alert({
      id: 'disconnect-alert',
      icon: true,
      content: this.disconnectAlertTemplate,
      keepAfterRouteChange: false,
      type: MbsPopupType.danger
    });

    console.log(`${new Date()}: showDisconnectAlert : ${this.disconnectAlert}`);

    this.alertService.alert(this.disconnectAlert);
  }

  showChat(): boolean {
    return this.chatVisibility.canShowChatByPermissions();
  }

  private showMaintenanceNotifications() {
    this.maintenanceService.getNotifications()?.forEach((notification) => {
      const alert = new Alert({
        id: 'maintenance-alert',
        icon: true,
        content: notification.message,
        ignoreRouteChanges: true,
        type: MbsPopupType.info,
        overlay: false
      });
      this.alertService.alert(alert);
    });
  }

  private showTimezoneAlert(user: Administrator): void {
    if (
      this.currentUser &&
      !this.currentUser.ProviderInfo.TimeZoneSpecified &&
      this.timezoneAlertTemplate &&
      !this.appPersistent.data.timeZoneAlertDismissed &&
      this.currentUser.IsWizardComplete &&
      !user.ProviderInfo.TimeZoneSpecified
    ) {
      this.alertService
        .alert(
          new Alert({
            content: this.timezoneAlertTemplate,
            keepAfterRouteChange: true,
            type: MbsPopupType.danger
          })
        )
        .subscribe(() => (this.appPersistent.data.timeZoneAlertDismissed = true));
    }
  }

  private showInstallAppBanner(): void {
    this.mobileApplicationsService.showInstallBanner(this.viewContainerRef);
  }
}
